import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const CurrencyContext = createContext();

const API_URL = process.env.REACT_APP_API_URL || 'https://hms.backend.hiragroupcloud.com';

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState({
    currencyCode: 'GBP',
    currencySymbol: '£',
    currencyName: 'British Pound'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userLabId, setUserLabId] = useState(null);

  // Get token from localStorage
  const getToken = () => {
    return localStorage.getItem('accessToken') || localStorage.getItem('token') || '';
  };

  // Get or create userLabId from localStorage
  const getOrCreateUserLabId = () => {
    let storedUserLabId = localStorage.getItem('userLabId');
    if (!storedUserLabId) {
      storedUserLabId = `LAB_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('userLabId', storedUserLabId);
    }
    return storedUserLabId;
  };

  const fetchCurrencyPreference = async (labId) => {
    try {
      setLoading(true);
      setError(null);

      const token = getToken();

      const response = await axios.get(`${API_URL}/api/currency/currency-preference`, {
        params: { userLabId: labId },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'userLabId': labId,
          'x-user-lab-id': labId
        }
      });

      setCurrency(response.data);
      return response.data;
    } catch (error) {
      // Fallback to GBP silently
      const defaultCurrency = {
        currencyCode: 'GBP',
        currencySymbol: '£',
        currencyName: 'British Pound'
      };
      setCurrency(defaultCurrency);
      return defaultCurrency;
    } finally {
      setLoading(false);
    }
  };

  const updateCurrencyPreference = async (newCurrency) => {
    try {
      const currentLabId = getOrCreateUserLabId();
      const token = getToken();

      setLoading(true);
      setError(null);

      const response = await axios.put(`${API_URL}/api/currency/currency-preference`, {
        currency: newCurrency,
        userLabId: currentLabId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'userLabId': currentLabId,
          'x-user-lab-id': currentLabId
        }
      });

      setCurrency(response.data.currency);

      // Also update the preferences in the database with the new currency
      try {
        await axios.post(`${API_URL}/api/preferences/`, {
          userLabId: currentLabId,
          currency: newCurrency
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'userLabId': currentLabId,
            'x-user-lab-id': currentLabId
          }
        });
      } catch (prefError) {
        // Silently ignore preference update errors
      }

      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update currency';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return '';

    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

    if (isNaN(numericAmount)) return '';

    // Format with currency symbol and 2 decimal places
    return `${currency.currencySymbol}${numericAmount.toFixed(2)}`;
  };

  const initializeCurrency = async () => {
    try {
      const labId = getOrCreateUserLabId();
      const token = getToken();
      setUserLabId(labId);

      // Create default preferences for this userLabId if they don't exist
      try {
        await axios.post(`${API_URL}/api/currency/create-default-preferences`, {
          userLabId: labId
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'userLabId': labId,
            'x-user-lab-id': labId
          }
        });
      } catch (prefError) {
        // Silently ignore - preferences might already exist
      }

      // Also ensure preferences exist in preferences collection
      try {
        await axios.post(`${API_URL}/api/preferences/`, {
          userLabId: labId,
          currency: 'GBP'
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'userLabId': labId,
            'x-user-lab-id': labId
          }
        });
      } catch (prefError) {
        // Silently ignore - preferences entry might already exist
      }

      // Fetch the currency preference for this userLabId
      await fetchCurrencyPreference(labId);
    } catch (error) {
      // Even if initialization fails, set default currency
      setCurrency({
        currencyCode: 'GBP',
        currencySymbol: '£',
        currencyName: 'British Pound'
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    initializeCurrency();
  }, []);

  // Wrapped refresh to catch errors and avoid unhandled rejections
  const refreshCurrency = async () => {
    try {
      const labId = getOrCreateUserLabId();
      await fetchCurrencyPreference(labId);
    } catch (error) {
      // Already handled inside fetchCurrencyPreference
    }
  };

  const clearError = () => setError(null);

  const value = {
    currency,
    loading,
    error,
    formatAmount,
    updateCurrencyPreference,
    refreshCurrency,
    clearError,
    userLabId
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};